import React from "react";
import {
  Eye, MousePointerClick, DollarSign, Users, Phone, Inbox, Ticket,
  TrendingUp, Radio, Megaphone, Globe, Percent, Target,
} from "lucide-react";

const money = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const num = (n) => (n == null ? "—" : Number(n).toLocaleString());

  const KPIS = (t) => {
  const list = [
  { label: "Impressions", value: num(t.impressions), icon: Eye },
  { label: "Reach", value: num(t.reach), icon: Users },
  { label: "Clicks", value: num(t.clicks), icon: MousePointerClick },
  { label: "CTR", value: (t.ctr ?? 0) + "%", icon: Percent },
  { label: "Ad spend", value: money(t.spend), icon: DollarSign, accent: true },
  { label: "Cost / click", value: t.cpc != null ? "$" + t.cpc : "—", icon: DollarSign },
  { label: "Store visits", value: num(t.visits), icon: MapVisits, accent: true },
  { label: "Cost / visit", value: t.cost_per_visit != null ? "$" + t.cost_per_visit : "—", icon: TrendingUp, good: true },
  { label: "Phone calls", value: num(t.calls), icon: Phone },
  { label: "Leads", value: num(t.leads), icon: Inbox },
  { label: "Cost / lead", value: t.cost_per_lead != null ? "$" + t.cost_per_lead : "—", icon: DollarSign },
  { label: "Redemptions", value: num(t.redemptions), icon: Ticket },
  { label: "Redemption rate", value: (t.redemption_rate ?? 0) + "%", icon: Percent, good: true },
  { label: "Visit conv. rate", value: (t.conversion_rate ?? 0) + "%", icon: Target, good: true },
  { label: "Live campaigns", value: num(t.live_campaigns), icon: Megaphone },
  ];
  if (t.visitation_lift != null) list.splice(7, 0, { label: "Visitation lift", value: t.visitation_lift + "%", icon: TrendingUp, good: true });
  return list;
};

function MapVisits(props) { return <Users {...props} />; }

const CH_ICON = { Radio, Audio: Radio, DOOH: Globe, Display: Globe, Native: Globe, Social: Users, Search: Target, CTV: Eye, Video: Eye };

export default function PerformanceDashboard({ analytics, accent = "#2563EB", range, onRangeChange }) {
  if (!analytics) return null;
  const t = analytics.totals || {};
  const channels = analytics.channels || [];
  const sources = analytics.sources || [];
  const trend = analytics.trend || [];
  const maxImpr = Math.max(1, ...channels.map((c) => c.impressions || 0));
  const maxTrend = Math.max(1, ...trend.map((p) => p.visits || 0));
  const RANGES = [["7", "7 days"], ["30", "30 days"], ["90", "90 days"]];
  const ds = t.data_source;
  const dsMap = {
    measured: ["Measured · footfall measurement", "#E7F6EC", "#067647"],
    modeled: ["Modeled estimate", "#EEF1F0", "#5B6B85"],
    representative: ["Representative data", "#FCF1DC", "#9A7412"],
  };
  const [dsLabel, dsBg, dsColor] = dsMap[ds] || dsMap.modeled;

  return (
    <div data-testid="performance-dashboard">
      {onRangeChange && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }} data-testid="perf-range">
          <div style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
            {RANGES.map(([r, label], i) => (
              <button key={r} onClick={() => onRangeChange(r)} data-testid={`range-${r}`}
                style={{
                  border: "none", borderLeft: i ? "1px solid var(--border)" : "none",
                  background: String(range) === r ? accent : "#fff",
                  color: String(range) === r ? "#fff" : "var(--navy)",
                  fontSize: 12, fontWeight: 700, padding: "7px 14px", cursor: "pointer",
                }}>{label}</button>
            ))}
          </div>
        </div>
      )}
      {ds && (
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }} data-testid="perf-data-source">
          <span className="sr-badge" style={{ background: dsBg, color: dsColor }}>{dsLabel}</span>
          {t.visitation_lift != null && <span className="sr-muted" style={{ fontSize: 12 }}>visitation lift {t.visitation_lift}%{t.footfall_significant === false ? " (not yet significant)" : ""}</span>}
        </div>
      )}
      {analytics.sample && (
        <div className="sr-card" style={{ background: "var(--blue-light)", border: "1px solid var(--blue)", marginBottom: 16 }} data-testid="perf-sample-note">
          <p className="sr-muted" style={{ fontSize: 13 }}>Representative performance data — illustrative figures shown until this client has live campaigns and verified events. Every number is labeled by evidence level in the full report.</p>
        </div>
      )}

      {/* KPI grid */}
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }} data-testid="perf-kpis">
        {KPIS(t).map((k) => (
          <div key={k.label} className="sr-card" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            style={k.accent ? { background: "var(--blue-light)", border: "1px solid var(--blue)" } : {}}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <k.icon size={13} style={{ color: k.good ? "#067647" : "var(--blue)" }} />
              <span className="sr-muted" style={{ fontSize: 12, fontWeight: 600 }}>{k.label}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.good ? "#067647" : "var(--navy)", marginTop: 4 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Visits trend */}
      <div className="sr-card" style={{ marginTop: 18 }} data-testid="perf-trend">
        <div className="sr-eyebrow" style={{ marginBottom: 14 }}><TrendingUp size={13} /> Store visits — last 8 weeks</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 130 }}>
          {trend.map((p, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--navy)" }}>{p.visits}</div>
              <div title={`${p.week}: ${p.visits} visits`}
                style={{ width: "100%", maxWidth: 30, height: `${Math.max(4, (p.visits / maxTrend) * 96)}px`,
                  background: accent, borderRadius: "4px 4px 0 0" }} />
              <div className="sr-muted" style={{ fontSize: 10 }}>{p.week}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16, marginTop: 18 }}>
        {/* Channel breakdown */}
        <div className="sr-card" data-testid="perf-channels">
          <div className="sr-eyebrow" style={{ marginBottom: 14 }}>Channel breakdown</div>
          <div style={{ display: "grid", gap: 12 }}>
            {channels.map((c) => {
              const Icon = CH_ICON[c.channel] || Globe;
              return (
                <div key={c.channel} data-testid={`channel-${c.channel.toLowerCase()}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, color: "var(--navy)" }}>
                      <Icon size={14} style={{ color: "var(--blue)" }} /> {c.channel}
                    </span>
                    <span className="sr-muted">{money(c.spend)} · {num(c.visits)} visits</span>
                  </div>
                  <div style={{ background: "var(--border)", borderRadius: 999, height: 8 }}>
                    <div style={{ width: `${(c.impressions / maxImpr) * 100}%`, height: 8, borderRadius: 999, background: accent }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Attributed source sites */}
        <div className="sr-card" data-testid="perf-sources">
          <div className="sr-eyebrow" style={{ marginBottom: 6 }}><Globe size={13} /> Where results came from</div>
          <p className="sr-muted" style={{ fontSize: 12, marginBottom: 10 }}>Top attributed sites &amp; environments this budget ran on.</p>
          <div style={{ display: "grid", gap: 2 }}>
            {sources.map((s) => (
              <div key={s.site} data-testid={`source-${s.site.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontWeight: 600, color: "var(--navy)", fontSize: 14 }}>{s.site}
                  <span className="sr-badge sr-badge-gray" style={{ marginLeft: 8, fontSize: 10 }}>{s.channel}</span>
                </span>
                <span className="sr-muted" style={{ fontSize: 13 }}>{num(s.impressions)} impr · {num(s.visits)} visits</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
